import { c as _c } from "react/compiler-runtime";
import { uniq } from 'lodash';
import { invalidateBookingLimits } from '../accounts/bookingLimitsCache';
import { RequestParams } from '@commutifi-fe/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enterpriseQueryKeys } from '../enterprises/hooks';
import { MutationConfig, QueryConfig } from '../../reactQuery';
import { AccountVehiclesBookings, BookingInvoicesParams, BookingProps, DetailedBooking, batchConfirmTerminations, batchTerminateBookings, batchUpdateBookings, fetchBookingInvoices, fetchFilledForm, fetchOneBooking, getAccountPasses, patchBooking, postAdminBooking, postBooking, postTerminateBooking, putAccountPasses, putBookingVehicles } from './api';
import { type BookingActivationEmail } from './types';
const defaultBookingRelations = ['plan', 'plan.merchant', 'plan.planFaqs', 'plan.service', 'plan.service.provider', 'plan.regions', 'plan.planBookingFields', 'paymentMethod', 'accountVehiclesBookings'];
export const bookingsQueryKeys = {
  all: ['bookings'] as const,
  detail: (bookingId: string | undefined, relations?: string[], queryParams?: Omit<RequestParams, 'relations'>) => ['bookings', bookingId, relations, queryParams ?? {}] as const,
  invoices: (bookingId: string | undefined, queryParams?: BookingInvoicesParams) => ['bookings', bookingId, 'invoices', queryParams ?? {}] as const,
  accountPasses: (bookingId: string | undefined, queryParams?: RequestParams) => ['bookings', bookingId, 'account', 'passes', queryParams ?? {}] as const
};
export function useBooking(bookingId, t0, options) {
  const $ = _c(14);
  let queryParams;
  let relations;
  let t1;
  let t2;
  if ($[0] !== bookingId || $[1] !== t0) {
    ({
      relations,
      ...queryParams
    } = t0 === undefined ? {} : t0);
    t2 = useQuery;
    t1 = bookingsQueryKeys.detail(bookingId, relations, queryParams);
    $[0] = bookingId;
    $[1] = t0;
    $[2] = queryParams;
    $[3] = relations;
    $[4] = t1;
    $[5] = t2;
  } else {
    queryParams = $[2];
    relations = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== bookingId || $[7] !== queryParams || $[8] !== relations) {
    t3 = async () => {
      if (!bookingId) {
        throw Error("Missing Booking id");
      }
      return fetchOneBooking(bookingId, {
        relations: uniq(defaultBookingRelations.concat(relations || [])),
        ...queryParams
      });
    };
    $[6] = bookingId;
    $[7] = queryParams;
    $[8] = relations;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== options || $[11] !== t1 || $[12] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t3,
      ...options
    };
    $[10] = options;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t2(t4);
}
export const useBookingInvoices = (bookingId, queryParams, options) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== bookingId || $[1] !== queryParams) {
    t0 = bookingsQueryKeys.invoices(bookingId, queryParams);
    $[0] = bookingId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== bookingId || $[4] !== queryParams) {
    t1 = async () => {
      if (!bookingId) {
        throw Error("Cannot fetch booking invoices without a bookingId parameter");
      }
      return fetchBookingInvoices(bookingId, {
        sort: "-createdAt",
        ...queryParams
      });
    };
    $[3] = bookingId;
    $[4] = queryParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
};
export function useAccountPasses(bookingId, t0, options) {
  const $ = _c(12);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== bookingId || $[3] !== queryParams) {
    t2 = bookingsQueryKeys.accountPasses(bookingId, queryParams);
    $[2] = bookingId;
    $[3] = queryParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== bookingId || $[6] !== queryParams) {
    t3 = async () => {
      if (!bookingId) {
        throw new Error("Cannot get booking passes without bookingId being defined");
      }
      return getAccountPasses(bookingId, queryParams);
    };
    $[5] = bookingId;
    $[6] = queryParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== options || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[8] = options;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
}
type UsePatchBookingOptions = MutationConfig<typeof patchBooking, {
  body: Parameters<typeof patchBooking>[1];
  queryParams?: Parameters<typeof patchBooking>[2];
}> & {
  /**
   * When provided, invalidates the enterprise's bookings queries on success (admin lists,
   * inventory timeline). Omit it to skip that invalidation entirely — commuter call sites
   * rely on this to avoid triggering enterprise-scoped refetches.
   */
  enterpriseId?: string;
};
export const usePatchBooking = (bookingId, options) => {
  const $ = _c(16);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== options) {
    t0 = options ?? {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let enterpriseId;
  let onSuccess;
  let restOptions;
  if ($[2] !== t0) {
    ({
      onSuccess,
      enterpriseId,
      ...restOptions
    } = t0);
    $[2] = t0;
    $[3] = enterpriseId;
    $[4] = onSuccess;
    $[5] = restOptions;
  } else {
    enterpriseId = $[3];
    onSuccess = $[4];
    restOptions = $[5];
  }
  let t1;
  if ($[6] !== bookingId) {
    t1 = t2 => {
      const {
        body,
        queryParams
      } = t2;
      if (!bookingId) {
        throw new Error("Cannot patch booking without bookingId being defined");
      }
      return patchBooking(bookingId, body, queryParams);
    };
    $[6] = bookingId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== enterpriseId || $[9] !== onSuccess || $[10] !== queryClient) {
    t2 = async (...t3) => {
      const args = t3;
      if (enterpriseId) {
        await queryClient.invalidateQueries({
          queryKey: enterpriseQueryKeys.bookings(enterpriseId)
        });
      }
      await onSuccess?.(...args);
    };
    $[8] = enterpriseId;
    $[9] = onSuccess;
    $[10] = queryClient;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== restOptions || $[13] !== t1 || $[14] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2,
      ...restOptions
    };
    $[12] = restOptions;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return useMutation(t3);
};
export const usePatchManyBookings = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePutBookingVehicles = (bookingId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== bookingId) {
    t0 = body => {
      if (!bookingId) {
        throw new Error("Cannot update booking vehicles without bookingId being defined");
      }
      return putBookingVehicles(bookingId, body);
    };
    $[0] = bookingId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePatchBookingPasses = (bookingId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== bookingId) {
    t0 = body => {
      if (!bookingId) {
        throw new Error("Cannot update booking passes without bookingId being defined");
      }
      return putAccountPasses(bookingId, body);
    };
    $[0] = bookingId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePostTerminateBooking = (bookingId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== bookingId) {
    t0 = body => {
      if (!bookingId || !body.endDate) {
        throw new Error("Cannot post a booking cancellation without bookingId or endDate being defined");
      }
      return postTerminateBooking(bookingId, body);
    };
    $[0] = bookingId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useBatchTerminateBookings = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp2,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useBatchConfirmTerminations = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp3,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useFillForm = (bookingId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== bookingId) {
    t0 = async () => {
      if (!bookingId) {
        throw Error("Missing bookingId id");
      }
      return fetchFilledForm(bookingId);
    };
    $[0] = bookingId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePostBooking = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp4,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePostAdminBooking = options => {
  const $ = _c(5);
  let t0;
  if ($[0] !== options) {
    t0 = (...t1) => {
      const args = t1;
      const [,, variables] = args;
      invalidateBookingLimits(variables?.accountId);
      options?.onSettled?.(...args);
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: _temp5,
      ...options,
      onSettled: t0
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
function _temp(t0) {
  const {
    ids,
    data,
    activationEmail
  } = t0;
  return batchUpdateBookings(ids, data, {
    activationEmail
  });
}
function _temp2(t0) {
  const {
    bookingsIds
  } = t0;
  return batchTerminateBookings(bookingsIds);
}
function _temp3(t0) {
  const {
    bookingIds
  } = t0;
  return batchConfirmTerminations(bookingIds);
}
function _temp4(body) {
  return postBooking(body);
}
function _temp5(body) {
  return postAdminBooking(body);
}